import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  NativeCommand,
  NativeCommandEnum,
  WebCommand,
  WebCommandEnum,
} from '../models/commands';
import paths from '~/app/paths/paths';
import { RoboticMowerBleConnectionStatus } from '../models/ble-connection-status';
import { NativeAppInfo } from '../models/native-version';

type WebViewStore = {
  onWebView?: boolean;
  onWebViewInitialized: boolean;
  nativeAppInfo?: NativeAppInfo;
  roboticMowerBleConnected?: RoboticMowerBleConnectionStatus;
  activeRoutePath: string;
  activeRouteSearch?: string;
  // Queued so multiple commands fired in one render tick aren't dropped —
  // a scalar slot would be overwritten before the consumer effect runs.
  nativeCommands: NativeCommand<NativeCommandEnum>[];
  webCommands: WebCommand<WebCommandEnum>[];
  // Command-key → expiresAt epoch ms for the in-flight duplicate-tap guard.
  inFlightNativeCommands: Record<string, number>;
};
export const defaultState: WebViewStore = {
  activeRoutePath: paths.dashboard.path,
  onWebViewInitialized: false,
  roboticMowerBleConnected: undefined,
  nativeAppInfo: undefined,
  nativeCommands: [],
  webCommands: [],
  inFlightNativeCommands: {},
};

const slice = createSlice({
  name: 'native',
  initialState: defaultState,
  reducers: {
    setOnWebView: (state, action: PayloadAction<NativeAppInfo>) => {
      state.onWebView = action.payload !== undefined;
      state.nativeAppInfo = action.payload;
    },
    setOnWebViewInitialized: (state) => {
      state.onWebViewInitialized = true;
    },
    setOnWebViewFalseIfUndefined: (state) => {
      if (state.onWebView === undefined) {
        state.onWebView = false;
      }
    },
    setRoboticMowerBleConnected: (
      state,
      action: PayloadAction<RoboticMowerBleConnectionStatus>
    ) => {
      state.roboticMowerBleConnected = action.payload;
    },
    setActiveRoutePath: (state, action) => {
      state.activeRoutePath = action.payload;
    },
    setActiveRouteSearch: (state, action) => {
      state.activeRouteSearch = action.payload;
    },
    dispatchNativeCommand: (state, action: PayloadAction<NativeCommand<NativeCommandEnum>>) => {
      state.nativeCommands ??= [];
      state.nativeCommands.push(action.payload);
    },
    dispatchWebCommand: (state, action: PayloadAction<WebCommand<WebCommandEnum>>) => {
      state.webCommands ??= [];
      state.webCommands.push(action.payload);
    },
    clearNativeCommand(state) {
      state.nativeCommands?.shift();
    },
    clearWebCommand(state) {
      state.webCommands?.shift();
    },
    drainAllCommands(state) {
      state.webCommands = [];
      state.nativeCommands = [];
      state.inFlightNativeCommands = {};
    },
    markNativeCommandInFlight(state, action: PayloadAction<{ key: string; expiresAt: number }>) {
      state.inFlightNativeCommands ??= {};
      state.inFlightNativeCommands[action.payload.key] = action.payload.expiresAt;
    },
    pruneExpiredInFlightCommands(state, action: PayloadAction<{ now: number }>) {
      if (!state.inFlightNativeCommands) {
        return;
      }
      for (const key of Object.keys(state.inFlightNativeCommands)) {
        if (state.inFlightNativeCommands[key] <= action.payload.now) {
          delete state.inFlightNativeCommands[key];
        }
      }
    },
  },
});

export const {
  setOnWebView,
  dispatchNativeCommand,
  dispatchWebCommand,
  clearWebCommand,
  clearNativeCommand,
  drainAllCommands,
  setActiveRoutePath,
  setActiveRouteSearch,
  setOnWebViewFalseIfUndefined,
  setOnWebViewInitialized,
  setRoboticMowerBleConnected,
  markNativeCommandInFlight,
  pruneExpiredInFlightCommands,
} = slice.actions;
export default slice.reducer;
